import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { SWReactIcons } from '@similarweb/icons';

import { useDebounce } from '../../hooks/useDebounce';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import { DotsLoader } from '../DotsLoader';

import { StyledSearchInputContainer, StyledInput, StyledClearIconContainer, StyledSearchIcon } from './styles';

type SearchTextBoxProps = {
  searchText: string;
  placeholder: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  autoFocus?: boolean;
  className?: string;
  debounceTime?: number;
  onChange: (searchText: string) => void;
  onClear?: (clearedString: string) => void;
};

export const SearchTextBox = (props: SearchTextBoxProps) => {
  const {
    searchText,
    placeholder,
    isDisabled = false,
    isLoading = false,
    autoFocus = true,
    debounceTime = 600,
    onChange,
    onClear,
  } = props;
  const [currentValue, setCurrentValue] = useState(searchText);

  const focusRef = useFocusOnMount();
  const plainRef = useRef<HTMLInputElement>(null);
  const inputRef = autoFocus ? focusRef : plainRef;

  const debouncedOnChange = useDebounce(onChange, debounceTime);

  const debouncedChangeHandler = useMemo(() => {
    if (debounceTime === 0) {
      return onChange;
    }

    return debouncedOnChange;
  }, [onChange, debounceTime, debouncedOnChange]);

  const handleClearSearch = () => {
    if (typeof onClear === 'function') {
      onClear(currentValue);
    }

    setCurrentValue('');
    onChange('');
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;

    if (value !== currentValue) {
      setCurrentValue(value);
      debouncedChangeHandler(value);
    }
  };

  useEffect(() => {
    setCurrentValue(searchText);
  }, [searchText]);

  return (
    <StyledSearchInputContainer className={props.className}>
      <StyledSearchIcon iconName="search" size="sm" />
      <StyledInput
        type="text"
        isDisabled={isDisabled}
        value={currentValue}
        disabled={isDisabled}
        onChange={handleChange}
        placeholder={placeholder}
        data-automation="results-search-field"
        ref={inputRef}
      />
      {isLoading ? <DotsLoader /> : null}
      {!isLoading && currentValue.length > 0 ? (
        <StyledClearIconContainer onClick={handleClearSearch} data-automation="ClearButton">
          <SWReactIcons iconName="clear" size="xs" />
        </StyledClearIconContainer>
      ) : null}
    </StyledSearchInputContainer>
  );
};
